import { queryClient } from './queryClient';
import { effectsOfWrite } from './writeEffects';
import api from '../services/api';
import { useAuthStore } from '../context/authStore';
import { useBrandingStore } from '../context/brandingStore';
import { useFeatureFlagStore } from '../context/featureFlagStore';

/**
 * A saved change shows on the next page opened, with no reload.
 *
 * Pages stay in the query cache for five minutes and a save only cleared its
 * own entry, so the quote page kept showing the account manager after Quote
 * layout had turned it off, until F5. Feature flags, branding and the user's
 * permissions were worse: read once per page load and never again.
 *
 * Pages nobody has open are dropped, so the next visit loads rather than
 * showing the old answer first. Pages on screen are only marked stale: a refetch
 * here would run each settings card's "copy what loaded into the form" effect
 * and overwrite what the admin has typed in the card next to the one just saved.
 *
 * Only this browser tab. Another tab or another person sees the change after
 * the cache's five minutes, or on a reload.
 */
const REFRESH_DELAY_MS = 200;
let pending = { flags: false, branding: false, account: false };
let timer = null;

function runRefreshes() {
  const due = pending;
  pending = { flags: false, branding: false, account: false };
  timer = null;
  if (due.flags) useFeatureFlagStore.getState().refreshFlags();
  if (due.branding) useBrandingStore.getState().refresh(api.defaults.baseURL);
  if (due.account) useAuthStore.getState().refreshAccount();
}

export function installFreshAfterWrite() {
  api.interceptors.response.use((response) => {
    const effects = effectsOfWrite(response.config?.method, response.config?.url);
    if (effects.sweep) {
      queryClient.removeQueries({ predicate: (query) => query.getObserversCount() === 0 });
      queryClient.invalidateQueries({ refetchType: 'none' });
    }
    if (effects.flags || effects.branding || effects.account) {
      pending = {
        flags: pending.flags || effects.flags,
        branding: pending.branding || effects.branding,
        account: pending.account || effects.account,
      };
      // Several toggles in a row are one refresh, and the last answer is the newest.
      if (timer) clearTimeout(timer);
      timer = setTimeout(runRefreshes, REFRESH_DELAY_MS);
    }
    return response;
  });
}
